import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "../../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import { toast } from "sonner";

const ROLE_OPTIONS = [
  { value: "buyer", label: "Collector / Buyer" },
  { value: "artist", label: "Artist" },
  { value: "art_center", label: "Art Hub" },
  { value: "journalist", label: "Journalist" },
  { value: "storyteller", label: "Storyteller" },
  { value: "writer", label: "Writer (general)" },
  { value: "admin", label: "Administrator" },
];

const CONTRIBUTOR_ROLES = ["artist", "art_center", "writer", "journalist", "storyteller"];
const PUBLISHABLE_ROLES = ["artist", "art_center"];

const labelCls = "label-eyebrow text-[#555]";
const inputCls =
  "mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111]";

/**
 * Admin-side full profile editor. Opens from /admin/users.
 * Updates flow through PATCH /api/admin/users/{id}.
 */
export const EditUserDialog = ({ user, open, onClose, onUpdated }) => {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  useEffect(() => {
    if (!user) {
      setForm(null);
      return;
    }
    setForm({
      name: user.name || "",
      email: user.email || "",
      role: user.role || "buyer",
      country: user.country || "",
      bio: user.bio || "",
      statement: user.statement || "",
      avatar_url: user.avatar_url || "",
      intro_video_url: user.intro_video_url || "",
      instagram_handle: user.instagram_handle || "",
      twitter_handle: user.twitter_handle || "",
      facebook_url: user.facebook_url || "",
      website_url: user.website_url || "",
      publish_status: user.publish_status || "draft",
    });
  }, [user]);

  if (!user || !form) return null;

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: r } = await api.post("/upload", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, avatar_url: r.url }));
      toast.success("Avatar uploaded");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed");
    } finally {
      setAvatarUploading(false);
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Send only the editable fields the admin endpoint accepts.
      const payload = {
        name: form.name,
        email: form.email,
        role: form.role,
        country: form.country,
        bio: form.bio,
        statement: form.statement,
        avatar_url: form.avatar_url,
        intro_video_url: form.intro_video_url,
        instagram_handle: form.instagram_handle,
        twitter_handle: form.twitter_handle,
        facebook_url: form.facebook_url,
        website_url: form.website_url,
      };
      if (PUBLISHABLE_ROLES.includes(form.role)) {
        payload.publish_status = form.publish_status;
      }
      const { data } = await api.patch(`/admin/users/${user.id}`, payload);
      toast.success("User updated");
      onUpdated?.(data);
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const isContributor = CONTRIBUTOR_ROLES.includes(form.role);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        data-testid="edit-user-dialog"
        className="rounded-none border border-[#E5E0D8] bg-white max-w-2xl max-h-[90vh] overflow-y-auto"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-3xl text-[#111] font-light">
            Edit user
          </DialogTitle>
          <DialogDescription className="text-sm text-[#555]">
            Update profile, role, and social handles. Changes are audit-logged.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5 mt-2">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Full name</label>
              <Input
                data-testid="edit-user-name"
                required
                value={form.name}
                onChange={set("name")}
                className={inputCls}
              />
            </div>
            <div>
              <label className={labelCls}>Email</label>
              <Input
                data-testid="edit-user-email"
                required
                type="email"
                value={form.email}
                onChange={set("email")}
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className={labelCls}>Role</label>
              <Select value={form.role} onValueChange={set("role")}>
                <SelectTrigger
                  data-testid="edit-user-role"
                  className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus:ring-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem
                      key={r.value}
                      value={r.value}
                      data-testid={`edit-user-role-${r.value}`}
                    >
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className={labelCls}>Country</label>
              <Input
                data-testid="edit-user-country"
                value={form.country}
                onChange={set("country")}
                className={inputCls}
              />
            </div>
          </div>

          {PUBLISHABLE_ROLES.includes(form.role) && (
            <div>
              <label className={labelCls}>
                Public visibility · {form.role === "art_center" ? "art hubs" : "artists"} only
              </label>
              <Select value={form.publish_status} onValueChange={set("publish_status")}>
                <SelectTrigger
                  data-testid="edit-user-publish"
                  className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 focus:ring-0"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="rounded-none">
                  <SelectItem value="draft">Draft — hidden from public site</SelectItem>
                  <SelectItem value="published">
                    Published — visible on {form.role === "art_center" ? "/art-hubs" : "/artists"}
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}

          <div>
            <label className={labelCls}>Avatar</label>
            <div className="mt-2 flex items-center gap-4">
              {form.avatar_url ? (
                <img
                  src={form.avatar_url}
                  alt=""
                  className="w-16 h-16 object-cover border border-[#E5E0D8] rounded-full"
                />
              ) : (
                <div className="w-16 h-16 bg-[#F4EFE6] border border-[#E5E0D8] rounded-full" />
              )}
              <div className="flex-1">
                <input
                  data-testid="edit-user-avatar-file"
                  type="file"
                  accept="image/*"
                  onChange={handleAvatarUpload}
                  disabled={avatarUploading}
                  className="text-xs"
                />
                {avatarUploading && (
                  <span className="ml-2 label-eyebrow text-[#A87900]">Uploading…</span>
                )}
              </div>
            </div>
            <Input
              data-testid="edit-user-avatar"
              value={form.avatar_url}
              onChange={set("avatar_url")}
              placeholder="…or paste an https:// or /api/files/… URL"
              className="mt-2 rounded-none border-b-2 border-l-0 border-r-0 border-t-0 border-[#E5E0D8] bg-transparent px-0 py-2 focus-visible:ring-0 focus-visible:border-[#111] text-xs"
            />
          </div>

          {form.role === "artist" && (
            <div data-testid="edit-user-intro-video-group">
              <label className={labelCls}>Introduction video · for homepage gallery</label>
              <Input
                data-testid="edit-user-intro-video"
                value={form.intro_video_url}
                onChange={set("intro_video_url")}
                placeholder="/api/files/… (upload via Submission portal) or https://youtube.com/…"
                className={inputCls}
              />
              {form.intro_video_url && form.intro_video_url.startsWith("/api/files/") && (
                <p className="mt-2 label-eyebrow text-[#1B5E20]">✓ Hosted video attached</p>
              )}
            </div>
          )}

          <div>
            <label className={labelCls}>Bio</label>
            <Textarea
              data-testid="edit-user-bio"
              rows={3}
              value={form.bio}
              onChange={set("bio")}
              className="mt-2 rounded-none border border-[#E5E0D8] bg-transparent focus-visible:ring-0 focus-visible:border-[#111]"
            />
          </div>

          {form.role === "artist" && (
            <div>
              <label className={labelCls}>Artist statement · long-form</label>
              <Textarea
                data-testid="edit-user-statement"
                rows={4}
                value={form.statement}
                onChange={set("statement")}
                className="mt-2 rounded-none border border-[#E5E0D8] bg-transparent focus-visible:ring-0 focus-visible:border-[#111]"
              />
            </div>
          )}

          {isContributor && (
            <fieldset className="border border-[#E5E0D8] p-4 space-y-4" data-testid="edit-user-socials">
              <legend className="label-eyebrow text-[#1E3A8A] px-2">Socials</legend>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Instagram handle</label>
                  <Input
                    data-testid="edit-user-instagram"
                    value={form.instagram_handle}
                    onChange={set("instagram_handle")}
                    placeholder="without the @"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>X / Twitter handle</label>
                  <Input
                    data-testid="edit-user-twitter"
                    value={form.twitter_handle}
                    onChange={set("twitter_handle")}
                    placeholder="without the @"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Facebook URL</label>
                  <Input
                    data-testid="edit-user-facebook"
                    value={form.facebook_url}
                    onChange={set("facebook_url")}
                    placeholder="https://facebook.com/…"
                    className={inputCls}
                  />
                </div>
                <div>
                  <label className={labelCls}>Website</label>
                  <Input
                    data-testid="edit-user-website"
                    value={form.website_url}
                    onChange={set("website_url")}
                    placeholder="https://…"
                    className={inputCls}
                  />
                </div>
              </div>
              <p className="text-xs text-[#555]">
                URLs must start with <span className="font-mono-aam">https://</span>.
              </p>
            </fieldset>
          )}

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="rounded-none border-[#E5E0D8] text-[#555] hover:text-[#111] px-5 py-3 h-auto label-eyebrow"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              data-testid="edit-user-submit"
              disabled={saving}
              className="rounded-none bg-[#1E3A8A] hover:bg-[#162a66] text-white px-6 py-3 h-auto label-eyebrow disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
